<script setup lang="ts">
import { defineAsyncComponent } from 'vue'

import { useDefaults } from '../../composables'
import {
	type BSnackbarEvents,
	type BSnackbarProps,
	type BSnackbarSlots,
	useSnackbar
} from './use-snackbar'

const BIcon = defineAsyncComponent(() => import('../BIcon/BIcon.vue'))
const BButton = defineAsyncComponent(() => import('../BButton/BButton.vue'))
const BProgress = defineAsyncComponent(
	() => import('../BProgress/BProgress.vue')
)

const _props = withDefaults(defineProps<BSnackbarProps>(), {
	closeIcon: 'cancel-01',
	advanced: false,
	teleportTo: 'body',
	hasProgress: true
})
const props = useDefaults(_props)

const emits = defineEmits<BSnackbarEvents>()
defineSlots<BSnackbarSlots>()
const { t, te } = useI18n()

const {
	handleClose,
	handleAction,
	classes,
	styles,
	iconName,
	computedProgress,
	showDescription,
	isVisible,
	handleMouseEnter,
	handleMouseLeave
} = useSnackbar(props, emits)
</script>

<template>
	<Teleport :to="teleportTo">
		<Transition
			name="fade-slide"
			@after-leave="$emit('after-leave')"
		>
			<div
				v-if="isVisible"
				:class="[classes, 'cursor-pointer']"
				:style="styles"
				role="alert"
				aria-live="polite"
				aria-atomic="true"
				@mouseenter="handleMouseEnter"
				@mouseleave="handleMouseLeave"
			>
				<div
					v-if="(icon === true || icon) && advanced"
					:style="{
						backgroundColor:
							color !== 'neutral'
								? `var(--color-${color})`
								: `var(--color-surface-variant-2)`
					}"
					class="size-8 rounded-full items-center justify-center hidden xs:flex me-4"
					aria-hidden="true"
				>
					<BIcon
						:color="`${color}-container`"
						:icon="iconName"
						size="24"
						class="align-middle"
					/>
				</div>

				<div class="flex-grow overflow-hidden">
					<slot>
						<span class="overflow-hidden text-ellipsis whitespace-nowrap">
							{{ title && te(title) ? t(title) : title }}
						</span>
						<span
							v-if="showDescription"
							class="text-body-1 block mt-1 secondary-text"
							data-test="description"
						>
							{{
								description && te(description) ? t(description) : description
							}}
						</span>
					</slot>
				</div>

				<div class="flex max-w-max shrink-0 items-center">
					<slot name="action">
						<BButton
							v-if="actionText"
							:text="actionText"
							variant="link"
							size="xs"
							class="ms-3"
							@click="handleAction"
						/>
					</slot>
				</div>

				<slot
					v-if="props.closable"
					name="close-icon"
				>
					<BIcon
						:icon="closeIcon"
						:color="
							color === 'neutral'
								? 'on-surface'
								: props.advanced
									? color
									: `on-${color}-container`
						"
						:size="props.advanced ? 16 : 12"
						class="cursor-pointer ms-3"
						@click="handleClose"
					/>
				</slot>

				<!-- Progress Bar -->
				<BProgress
					v-if="hasProgress"
					:progress="computedProgress"
					:color="
						color === 'neutral'
							? 'surface-variant-2'
							: props.advanced
								? color
								: `on-${color}-container`
					"
					:bg-color="`var(--color-${color}-container)`"
					:thickness="3"
					variant="linear"
					size="100%"
					class="absolute bottom-0 left-0 w-full"
				/>
			</div>
		</Transition>
	</Teleport>
</template>

<style scoped>
.fade-slide-enter-active,
.fade-slide-leave-active {
	transition: all 0.3s ease;
}
.fade-slide-enter-from,
.fade-slide-leave-to {
	opacity: 0;
	transform: translateY(1rem);
}
</style>
